import { useState, useEffect } from 'react';
import { cn } from '../../lib/utils';

export interface TailwindDatePickerProps {
  value?: Date | null;
  onChange?: (date: Date | null) => void;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  /**
   * Display-only mode: the date is shown but cannot be edited (e.g. when the
   * value is inherited from a referenced timing). Visually distinct from
   * `disabled` (which signals lack of write rights) — uses a locked look with
   * a lock icon to communicate "non-editable because inherited".
   */
  readOnly?: boolean;
  className?: string;
  error?: string;
  minDate?: Date;
  maxDate?: Date;
}

/** Format a Date to YYYY-MM-DD for the native input */
const toInputValue = (d: Date | null | undefined): string => {
  if (!d) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

/** Parse a YYYY-MM-DD string into a local Date (avoids UTC shift) */
const fromInputValue = (s: string): Date | null => {
  if (!s) return null;
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, m - 1, d);
};

export default function TailwindDatePicker({
  value,
  onChange,
  label,
  placeholder,
  disabled = false,
  readOnly = false,
  className,
  error,
  minDate,
  maxDate,
}: TailwindDatePickerProps) {
  const [localValue, setLocalValue] = useState(toInputValue(value));

  useEffect(() => {
    setLocalValue(toInputValue(value));
  }, [value?.getTime?.()]);

  const commitValue = () => {
    const parsed = fromInputValue(localValue);
    if (toInputValue(parsed) !== toInputValue(value)) {
      onChange?.(parsed);
    }
  };

  // Display-only rendering: a non-interactive, visibly locked box. The dashed
  // border + muted fill + lock icon set it apart from both the editable input
  // and the `disabled` (no write rights) state.
  if (readOnly) {
    return (
      <div className={cn('flex flex-col gap-1.5', className)}>
        {label && (
          <label className="text-[11px] font-bold text-slate-500 uppercase tracking-widest px-1">
            {label}
          </label>
        )}
        <div className="relative">
          <div
            className="w-full flex items-center justify-between bg-slate-100 border border-dashed border-slate-300 text-slate-500 text-sm rounded-lg px-4 py-2.5 font-medium cursor-not-allowed select-none"
            title="Inherited from the referenced timing"
          >
            <span>{localValue || placeholder || '—'}</span>
            <span className="material-symbols-outlined text-[16px] text-slate-400">lock</span>
          </div>
        </div>
        {error && <p className="text-xs text-red-600 px-1">{error}</p>}
      </div>
    );
  }

  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      {label && (
        <label className="text-[11px] font-bold text-slate-500 uppercase tracking-widest px-1">
          {label}
        </label>
      )}

      <div className="relative">
        <input
          type="date"
          value={localValue}
          onChange={(e) => setLocalValue(e.target.value)}
          onBlur={commitValue}
          onKeyDown={(e) => { if (e.key === 'Enter') commitValue(); }}
          min={minDate ? toInputValue(minDate) : undefined}
          max={maxDate ? toInputValue(maxDate) : undefined}
          disabled={disabled}
          placeholder={placeholder}
          className={cn(
            'w-full appearance-none bg-slate-50 border text-slate-900 text-sm rounded-lg px-4 py-2.5 font-medium transition-colors',
            'focus:ring-2 focus:ring-primary focus:border-primary focus:outline-none',
            'hover:border-primary',
            error ? 'border-red-500' : 'border-slate-200',
            disabled && 'opacity-50 cursor-not-allowed',
          )}
        />
      </div>

      {error && (
        <p className="text-xs text-red-600 px-1">{error}</p>
      )}
    </div>
  );
}
